import React, { StrictMode } from 'react';
import { hydrate } from 'react-dom';
import { Provider } from 'react-redux';
import { fromJS } from 'immutable';
import { createBrowserHistory } from 'history';
import { Router } from 'react-router-dom';
import 'svgxuse';
import { EventReplayer } from 'preboot/replay';
import { UserAgentProvider } from '@quentin-sommer/react-useragent';
import { FacebookProvider } from 'react-facebook';
import { HelmetProvider } from 'react-helmet-async';
import StyleContext from 'isomorphic-style-loader/StyleContext';
import { loadableReady } from '@loadable/component';
import { configure as configureReactHotkeys } from 'react-hotkeys';

import initSentry from './client-sentry';
import Routes from './routes';
import ErrorBoundary from './common/components/ErrorBoundary';
import { ErrorReporter } from './core/devUtils';
import createBrowserLogger from './common/logger/browserLogger';
import configureStore from './store';
import { SiftScience } from './common/siftscience';
import { trackPageViewFacebook } from './common/tracking/facebook';
import { PubSubProvider } from './pubsub/hook/usePubsub';
import AllowedHostProvider from './common/components/AllowedHostsProvider';

// Init Sentry as soon as possible in the app, to make sure it can monitor everything.
initSentry();

// Get a hook on history
const history = createBrowserHistory();

// Global configuration for react-hotkeys. Namely telling
// it to listen to events from inputs.
configureReactHotkeys({
  ignoreTags: [],
  // To be honest, I have no idea what a simulated keypress event is
  // or why it exists. However, if this is left as its default (true),
  // the send message form ignores you after "enter" is pressed.
  simulateMissingKeyPressEvents: false,
});

// Set up SiftScience
SiftScience.initialize(__SIFTSCIENCE_SNIPPET_KEY__);

// attach tracking listeners
history.listen(() => {
  // Tracking pageviews should be done on every location change.

  // SiftScience
  SiftScience.push(['_trackPageview']);

  // Facebook
  trackPageViewFacebook();

  // Pinterest
  if (typeof window !== 'undefined' && window.pintrk) {
    window.pintrk('track', 'pagevisit');
  }
});

// Enables critical path CSS rendering
// https://github.com/kriasoft/isomorphic-style-loader
const insertCss = (...styles) => {
  // eslint-disable-next-line no-underscore-dangle
  const removeCss = styles.map(style => style._insertCss());
  return () => { removeCss.forEach(dispose => dispose()); };
};

/**
 * Conditionally wraps the redux state in immutable data structures.
 * @param {string} key
 * @param {Record<string, unknown>} state
 * @return {unknown}
 */
const maybeConvertToImmutable = (key, state) => {
  const nonImmutableKeys = ['instabook', 'featureFlags', 'currencies', 'requestCurrency', 'countryCode', 'locale'];
  return nonImmutableKeys.includes(key) ? state[key] : fromJS(state[key]);
};

/**
 * This is to hack around the fact that our store structure has a top-level
 * POJO, but some child values are immutable objects.
 * @param {Record<string, unknown>} state
 * @returns {Record<string, unknown>}
 */
const immutableizeJSState = state => Object.keys(state)
  .reduce((newState, key) => ({
    ...newState,
    [key]: maybeConvertToImmutable(key, state),
  }), {});
// This takes the state that was rendered on the server
// side and deserializes it.
const store = configureStore(immutableizeJSState(window.APP_STATE), createBrowserLogger);

// Delay hydration until all scripts are ready. It's unclear why this is necessary,
// given that script chunks are loaded with `async`, but it's in the loadable-components
// docs.
loadableReady(() => {
  hydrate(
    <Provider store={store}>
      <StrictMode>
        <Router history={history}>
          <HelmetProvider>
            {/* eslint-disable-next-line react/jsx-no-constructed-context-values */}
            <StyleContext.Provider value={{ insertCss }}>
              <UserAgentProvider ua={window.navigator.userAgent}>
                <FacebookProvider appId={__FB_APP_ID__} wait>
                  <ErrorBoundary ErrorComponent={ErrorReporter}>
                    <PubSubProvider>
                      <AllowedHostProvider>
                        <Routes />
                      </AllowedHostProvider>
                    </PubSubProvider>
                  </ErrorBoundary>
                </FacebookProvider>
              </UserAgentProvider>
            </StyleContext.Provider>
          </HelmetProvider>
        </Router>
      </StrictMode>
    </Provider>,
    document.getElementById('app'),
  );

  // Play back events that have been captured by preboot
  const replayer = new EventReplayer();
  replayer.replayAll();
});
